import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Truck, ShieldCheck, PackageCheck, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { cjProduct } from "../../lib/cj";
import { api } from "../../lib/api";
import { useCjCart } from "../../context/CjCartContext";
import { useSeo } from "../../hooks/useSeo";
import { MarketRail } from "../../components/market/MarketRail";

const money = (v) => `$${Number(v || 0).toFixed(2)}`;

const MarketProduct = () => {
  const { handle } = useParams();
  const [data, setData] = useState(null);
  const [active, setActive] = useState(0);
  const [err, setErr] = useState(false);
  const { add } = useCjCart();

  useEffect(() => {
    setData(null); setActive(0); setErr(false);
    window.scrollTo(0, 0);
    cjProduct(handle).then(setData).catch(() => setData({ product: null, related: [] }));
    api.post(`/cj/track/view/${handle}`).catch(() => {});
  }, [handle]);

  const p = data?.product;
  useSeo({
    title: p ? `${p.title} | TAVEN Marketplace` : "TAVEN Marketplace",
    description: p ? (p.description || p.title).slice(0, 160) : undefined,
    jsonLd: p ? {
      "@context": "https://schema.org", "@type": "Product", name: p.title,
      image: p.images?.length ? p.images : [p.image], description: (p.description || p.title).slice(0, 500),
      brand: { "@type": "Brand", name: "TAVEN" },
      offers: { "@type": "Offer", priceCurrency: "USD", price: p.price,
        availability: p.availability === "sold_out" ? "https://schema.org/OutOfStock" : "https://schema.org/InStock" },
    } : null,
  });

  if (!data) return <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 text-center text-muted-foreground">Loading…</div>;
  if (!p) return <div className="max-w-7xl mx-auto px-4 md:px-8 py-16 text-center">Product not found. <Link to="/market" className="text-taven-gold font-bold">Back to marketplace</Link></div>;

  const images = (p.images && p.images.length ? p.images : [p.image]).filter(Boolean);
  const main = images[active] || p.image;
  const availability = p.availability || "in_stock";
  // Required inventory-safe behavior:
  //  in_stock -> purchasable; pending (null/stale/unverified) -> browsable but Add-to-Cart BLOCKED;
  //  sold_out -> unavailable. Nothing unverified can be purchased (checkout stays fail-closed).
  const canBuy = availability === "in_stock" && p.purchasable !== false;

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8" data-testid="market-product">
      <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
        <div>
          <div className="aspect-square bg-white rounded-xl overflow-hidden border border-black/5">
            {main && !err ? (
              <img src={main} alt={p.title} onError={() => setErr(true)} className="w-full h-full object-cover" data-testid="market-product-image" />
            ) : <div className="w-full h-full flex items-center justify-center bg-taven-gray font-display font-black tracking-widest text-muted-foreground">TAVEN</div>}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 mt-3 overflow-x-auto">
              {images.map((im, i) => (
                <button key={i} onClick={() => { setActive(i); setErr(false); }}
                  className={`w-16 h-16 rounded-lg overflow-hidden border-2 shrink-0 ${i === active ? "border-taven-gold" : "border-transparent"}`}>
                  <img src={im} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          )}
        </div>

        <div>
          <h1 className="text-2xl md:text-3xl font-black font-display tracking-tight leading-tight" data-testid="market-product-title">{p.title}</h1>
          <div className="flex items-baseline gap-3 mt-4">
            <span className="text-3xl font-black" data-testid="market-product-price">{money(p.price)}</span>
          </div>
          <div className="flex flex-col gap-2 mt-6 text-sm" data-testid="market-availability">
            <span className="inline-flex items-center gap-2 text-muted-foreground"><Truck className="w-4 h-4 text-taven-gold" /> Ships from a US warehouse</span>
            {availability === "in_stock" && (
              <>
                <span className="inline-flex items-center gap-2 text-muted-foreground" data-testid="availability-in-stock"><PackageCheck className="w-4 h-4 text-taven-gold" /> In stock &amp; quality-checked</span>
                <span className="inline-flex items-center gap-2 text-muted-foreground"><ShieldCheck className="w-4 h-4 text-taven-gold" /> TAVEN quality &amp; compliance approved</span>
              </>
            )}
            {availability === "unverified" || availability === "pending" ? (
              <span className="inline-flex items-center gap-2 text-amber-600 font-semibold" data-testid="availability-verifying"><ShieldCheck className="w-4 h-4" /> Stock verification pending</span>
            ) : null}
            {availability === "sold_out" && (
              <span className="inline-flex items-center gap-2 text-red-600 font-semibold" data-testid="availability-sold-out"><PackageCheck className="w-4 h-4" /> Currently unavailable</span>
            )}
          </div>
          <button data-testid="market-add-to-cart"
            disabled={!canBuy}
            onClick={() => { if (!canBuy) return; add(p, 1); toast.success("Added to cart"); }}
            className={`mt-6 w-full sm:w-auto inline-flex items-center justify-center gap-2 h-12 px-8 rounded-full text-sm font-bold uppercase tracking-wider transition-colors ${canBuy ? "bg-black text-white hover:bg-taven-gold hover:text-black" : "bg-black/30 text-white cursor-not-allowed"}`}>
            <ShoppingBag className="w-4 h-4" /> {canBuy ? "Add to Cart" : availability === "sold_out" ? "Unavailable" : "Verification Pending"}
          </button>
          {(availability === "pending" || availability === "unverified") && (
            <p className="mt-2 text-xs text-amber-600" data-testid="market-stock-note">This item is being stock-verified and can't be purchased yet.</p>
          )}
          {p.description && (
            <div className="mt-6">
              <h2 className="text-sm font-bold uppercase tracking-wider mb-2">Description</h2>
              <p className="text-sm text-muted-foreground leading-relaxed whitespace-pre-line">{p.description}</p>
            </div>
          )}
        </div>
      </div>

      {data.related?.length > 0 && (
        <div className="mt-14">
          <MarketRail title="Related Products" products={data.related} />
        </div>
      )}
    </div>
  );
};

export default MarketProduct;
